import React, { useState, useEffect, useCallback } from 'react';
import { signOut, deleteUser, updateProfile } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, deleteDoc, collectionGroup, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import ChildLinking from './ChildLinking';
import ParentGradesView from './ParentGradesView';
import ParentAttendanceView from './ParentAttendanceView';
import ParentAssignmentsView from './ParentAssignmentsView';
import ParentMessagesView from './ParentMessagesView';
import ParentStoreView from './ParentStoreView';
import ParentTimetableView from './ParentTimetableView';
import ParentOnlineClassesView from './ParentOnlineClassesView';
import ParentNotificationsView from './ParentNotificationsView';
import EventDetailView from './EventDetailView';
import StudentBehaviorView from './StudentBehaviorView';
import { useSchoolName } from './useSchoolName';
import './ParentDashboard.css';
import './ParentDetailPages.css';

function ParentDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [mobileTab, setMobileTab] = useState(0);
  const [parentData, setParentData] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChildIndex, setSelectedChildIndex] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const schoolName = useSchoolName(parentData?.schoolId);
  const [loading, setLoading] = useState(true);

  const loadParentData = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Load parent document directly by UID (matching iOS approach)
      const parentDoc = await getDoc(doc(db, 'parents', user.uid));
      
      if (parentDoc.exists()) {
        const data = parentDoc.data();
        console.log('Parent data loaded:', data);
        setParentData(data);

        if (data.linkedChildrenIds && data.linkedChildrenIds.length > 0) {
          console.log('🔥 Loading children with IDs:', data.linkedChildrenIds);
          const childrenData = [];
          const linkedIds = data.linkedChildrenIds.filter(id => id);
          
          if (linkedIds.length === 0) {
            setChildren([]);
            return;
          }
          
          // Use the same approach as iOS - collectionGroup query for students
          // This matches the iOS verifyStudentExists and loadStudentsByIds methods
          try {
            const studentsQuery = query(collectionGroup(db, 'students'), where('studentId', 'in', linkedIds));
            const studentsSnapshot = await getDocs(studentsQuery);
            
            console.log('🔥 Students query completed - snapshot count:', studentsSnapshot.docs.length);
            
            studentsSnapshot.forEach(doc => {
              const studentData = doc.data();
              console.log('🔥 Student document data:', studentData);
              
              // Create fullName if needed (matching iOS approach)
              if (!studentData.fullName && studentData.firstName) {
                studentData.fullName = `${studentData.firstName} ${studentData.lastName || ''}`.trim();
              }
              
              // iOS uses the `id` field stored in the document data as the student UUID.
              // Only fall back to the Firestore document ID if the data field is missing.
              if (!studentData.id) {
                studentData.id = doc.id;
              }
              
              childrenData.push(studentData);
              console.log('🔥 Student data loaded:', studentData);
            });
            
            console.log('🔥 Final children data:', childrenData);
            setChildren(childrenData);
          } catch (error) {
            console.error('🔥 Error loading students with collectionGroup:', error);
            // Fallback to individual queries if collectionGroup fails
            for (const childId of data.linkedChildrenIds) {
              console.log('🔥 Fallback: Loading student:', childId);
              
              // Try class subcollections (this is where students are stored according to iOS)
              const classesSnapshot = await getDocs(collection(db, 'classes'));
              for (const classDoc of classesSnapshot.docs) {
                const studentsSubcollection = collection(db, 'classes', classDoc.id, 'students');
                const studentQuery = query(studentsSubcollection, where('studentId', '==', childId));
                const studentSnapshot = await getDocs(studentQuery);
                if (!studentSnapshot.empty) {
                  const studentData = studentSnapshot.docs[0].data();
                  if (!studentData.id) {
                    studentData.id = studentSnapshot.docs[0].id;
                  }
                  
                  if (!studentData.fullName && studentData.firstName) {
                    studentData.fullName = `${studentData.firstName} ${studentData.lastName || ''}`.trim();
                  }
                  
                  childrenData.push(studentData);
                  console.log('🔥 Fallback: Student data loaded:', studentData);
                  break;
                }
              }
            }
            setChildren(childrenData);
          }
        }
      } else {
        console.log('No parent document found for UID:', user.uid);
      }
    } catch (error) {
      console.error('Error loading parent data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadParentData();
  }, [loadParentData]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const tabs = [
    { title: 'Home', icon: '🏠' },
    { title: 'Child', icon: '👨‍👩‍👧' },
    { title: 'Messages', icon: '💬' },
    { title: 'Store', icon: '🛍️' },
    { title: 'Profile', icon: '👤' }
  ];

  const mobileTabs = [
    { title: 'Home', icon: '🏠' },
    { title: 'Child', icon: '👨‍👩‍👧' },
    { title: 'Messages', icon: '💬' },
    { title: 'Store', icon: '🛍️' },
    { title: 'Profile', icon: '👤' },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-brand">
          <img src="/logo512.png" alt="KN Education Portal logo" className="header-logo" />
          <div>
            <h1>KN Education Portal</h1>
            {schoolName && <span className="header-school">{schoolName}</span>}
          </div>
        </div>
        <div className="header-actions">
          <button
            className="notification-bell-button"
            onClick={() => {
              console.log('🔥 Notifications bell tapped');
              setShowNotifications(true);
            }}
            title="Notifications"
          >
            🔔
          </button>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <nav className="sidebar">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`tab-button ${selectedTab === index ? 'active' : ''}`}
              onClick={() => { setShowNotifications(false); setSelectedTab(index); }}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-title">{tab.title}</span>
            </button>
          ))}
        </nav>

        <main className="main-content">
          {showNotifications ? (
            <div className="detail-page">
              <button className="detail-back-button" onClick={() => setShowNotifications(false)}>
                ◄ Back
              </button>
              <ParentNotificationsView />
            </div>
          ) : (
            <>
              {selectedTab === 0 && <DashboardTab parentData={parentData} children={children} onOpenMessages={() => setSelectedTab(2)} onOpenChild={(i) => { setSelectedChildIndex(i); setSelectedTab(1); }} />}
              {selectedTab === 1 && <ChildTab children={children} parentData={parentData} selectedChildIndex={selectedChildIndex} onSelectChild={setSelectedChildIndex} />}
              {selectedTab === 2 && <MessagesTab parentData={parentData} />}
              {selectedTab === 3 && <ParentStoreView children={children} parentData={parentData} />}
              {selectedTab === 4 && <ProfileTab parentData={parentData} children={children} />}
            </>
          )}
        </main>

        <main className="mobile-main-content">
          {showNotifications ? (
            <div className="detail-page">
              <button className="detail-back-button" onClick={() => setShowNotifications(false)}>
                ◄ Back
              </button>
              <ParentNotificationsView />
            </div>
          ) : (
            <>
              {mobileTab === 0 && <DashboardTab parentData={parentData} children={children} onOpenMessages={() => setMobileTab(2)} onOpenChild={(i) => { setSelectedChildIndex(i); setMobileTab(1); }} />}
              {mobileTab === 1 && <ChildTab children={children} parentData={parentData} selectedChildIndex={selectedChildIndex} onSelectChild={setSelectedChildIndex} />}
              {mobileTab === 2 && <MessagesTab parentData={parentData} />}
              {mobileTab === 3 && <ParentStoreView children={children} parentData={parentData} />}
              {mobileTab === 4 && <ProfileTab parentData={parentData} children={children} />}
            </>
          )}
        </main>
      </div>

      {/* Mobile Bottom Tab Bar - iOS style */}
      <nav className="mobile-bottom-nav">
        {mobileTabs.map((tab, index) => (
          <button
            key={index}
            className={`mobile-tab-button ${mobileTab === index ? 'active' : ''}`}
            onClick={() => { setShowNotifications(false); setMobileTab(index); }}
          >
            <span className="mobile-tab-icon">{tab.icon}</span>
            <span className="mobile-tab-title">{tab.title}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function DashboardTab({ parentData, children, onOpenMessages, onOpenChild }) {
  const [childrenWithClassInfo, setChildrenWithClassInfo] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [stats, setStats] = useState({ totalGrades: 0, totalAttendance: 0, recentGrades: 0 });
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Load class information, statistics, and events
  useEffect(() => {
    const loadData = async () => {
    // Load events
    try {
      const eventsQuery = query(collection(db, 'events'), where('isActive', '==', true));
      const eventsSnapshot = await getDocs(eventsQuery);
      const now = new Date();
      const allEvents = eventsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }));

      const upcoming = allEvents
        .filter(event => {
          const eventDate = event.date?.toDate ? event.date.toDate() : new Date(event.date);
          return eventDate >= now;
        })
        .sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateA - dateB;
        });

      const past = allEvents
        .filter(event => {
          const eventDate = event.date?.toDate ? event.date.toDate() : new Date(event.date);
          return eventDate < now;
        })
        .sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateB - dateA;
        });

      setUpcomingEvents(upcoming.slice(0, 3));
      setPastEvents(past.slice(0, 3));
    } catch (error) {
      console.error('Error loading events:', error);
    }

      if (children.length === 0) return;
      
      console.log('🏠 HOME: Loading class info and stats for', children.length, 'children');
      const childrenWithInfo = [];
      let totalGrades = 0;
      let totalAttendance = 0;
      let recentGrades = 0;
      
      for (const child of children) {
        console.log('🏠 HOME: Processing child:', child.fullName, '- classId:', child.classId);
        
        let childWithClass = { ...child };
        
        if (child.classId) {
          // Load class info using the service
          console.log('🏠 HOME: 🏫 Using ClassInfoService for:', child.classId);
          
          try {
            const classData = await classInfoService.getClassInfo(child.classId);
            console.log('🏠 HOME: ✅ Class info from service:', classData);
            console.log('🏠 HOME: ✅ Source:', classData.source);
            console.log('🏠 HOME: ✅ Class name:', classData.name);
            console.log('🏠 HOME: ✅ Class grade:', classData.grade);
            
            childWithClass.className = classData.name;
            childWithClass.classGrade = classData.grade;
            childWithClass.teacherId = classData.teacherId;
            childWithClass.teacherName = classData.teacherName;
            childWithClass.subjectTeachers = classData.subjectTeachers || [];
            
            console.log('🏠 HOME: ✅ Class info loaded:', childWithClass.className);
          } catch (error) {
            console.log('🏠 HOME: ❌ Error from ClassInfoService:', error);
            childWithClass.className = 'Class ' + child.classId.slice(-8);
            childWithClass.classGrade = 'N/A';
          }
          
          // Load grades for this child (separate from class info loading)
          try {
            console.log('🏠 HOME: 📚 Loading grades for child:', child.fullName, 'classId:', child.classId);
            console.log('🏠 HOME: 📚 Child document ID:', child.id, 'studentId:', child.studentId);
            
            const assignmentsSubcollection = collection(db, 'classes', child.classId, 'assignments');
            const assignmentsSnapshot = await getDocs(assignmentsSubcollection);
            
            console.log('🏠 HOME: 📚 Found', assignmentsSnapshot.docs.length, 'assignments for', child.fullName);
            
            let childGrades = 0;
            for (const assignmentDoc of assignmentsSnapshot.docs) {
              const assignmentData = assignmentDoc.data();
              console.log('🏠 HOME: 📚 Checking assignment:', assignmentData.title, 'ID:', assignmentDoc.id);
              
              const gradesSubcollection = collection(assignmentDoc.ref, 'grades');
              const gradesSnapshot = await getDocs(gradesSubcollection);
              
              console.log('🏠 HOME: 📚 Found', gradesSnapshot.docs.length, 'grades for assignment:', assignmentData.title);
              
              for (const gradeDoc of gradesSnapshot.docs) {
                const gradeData = gradeDoc.data();
                console.log('🏠 HOME: 📚 Grade data:', gradeData);
                console.log('🏠 HOME: 📚 Grade document ID:', gradeDoc.id);
                console.log('🏠 HOME: 📚 All grade fields:', Object.keys(gradeData));
                
                // Check if grade matches this student using child.id (UUID) or child.studentId (6-digit)
                const gradeStudentId = gradeData.studentId;
                if (gradeStudentId === child.id || gradeStudentId === child.studentId) {
                  childGrades++;
                  totalGrades++;
                  console.log('🏠 HOME: ✅ Found grade for', child.fullName, 'Total now:', totalGrades);
                  
                  // Check if grade is recent (within last 7 days)
                  const gradedAt = gradeData.gradedAt || gradeData.createdAt || gradeData.updatedAt;
                  if (gradedAt) {
                    const gradeDate = new Date(gradedAt.toDate ? gradedAt.toDate() : gradedAt);
                    const daysAgo = Math.floor((new Date() - gradeDate) / (1000 * 60 * 60 * 24));
                    if (daysAgo <= 7) {
                      recentGrades++;
                    }
                  }
                }
              }
            }
            
            console.log('🏠 HOME: 📊 Child', child.fullName, 'has', childGrades, 'grades');
            
            // Load attendance for this child
            try {
              const attendanceSubcollection = collection(db, 'classes', child.classId, 'attendanceRecords');
              const attendanceSnapshot = await getDocs(attendanceSubcollection);
              
              let childAttendance = 0;
              for (const attendanceDoc of attendanceSnapshot.docs) {
                const attendanceData = attendanceDoc.data();
                const recordStudentId = attendanceData.studentId;
                if (recordStudentId === child.id || recordStudentId === child.studentId) {
                  childAttendance++;
                  totalAttendance++;
                }
              }
              
              console.log('🏠 HOME: 📊 Child', child.fullName, 'has', childAttendance, 'attendance records');
            } catch (attendanceError) {
              console.log('🏠 HOME: ❌ Error loading attendance for', child.fullName, attendanceError);
            }
          } catch (gradesError) {
            console.log('🏠 HOME: ❌ Error loading grades for', child.fullName, gradesError);
          }
        } else {
          childWithClass.className = 'Not assigned';
          childWithClass.classGrade = 'N/A';
        }
        
        childrenWithInfo.push(childWithClass);
      }
      
      console.log('🏠 HOME: 📊 Total stats - Grades:', totalGrades, 'Attendance:', totalAttendance, 'Recent:', recentGrades);
      setStats({ totalGrades, totalAttendance, recentGrades });
      console.log('🏠 HOME: ✅ Final children with class info:', childrenWithInfo);
      setChildrenWithClassInfo(childrenWithInfo);
    };
    
    loadData();
  }, [children]);

  // Full-screen event detail page (iOS ParentEventDetailView parity)
  if (selectedEvent) {
    return (
      <div className="detail-page">
        <button className="detail-back-button" onClick={() => setSelectedEvent(null)}>
          ◄ Back to Home
        </button>
        <EventDetailView event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="welcome-section">
        <h1>Welcome, {parentData?.firstName ? parentData.firstName + ' ' + (parentData?.lastName || '') : 'Parent'}</h1>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👨‍👩‍👧‍👦</div>
            <div className="stat-info">
              <h3>{children.length}</h3>
              <p>My Children</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📚</div>
            <div className="stat-info">
              <h3>{stats.totalGrades}</h3>
              <p>Total Grades</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-info">
              <h3>{stats.recentGrades}</h3>
              <p>Recent Grades</p>
            </div>
          </div>
        </div>
      </div>
      
      {children.length > 0 && (
        <>
          <div className="children-overview">
            <h3>My Children</h3>
            <div className="children-grid">
              {childrenWithClassInfo.map((child, index) => (
                <div
                  key={index}
                  className={`child-overview-card clickable ${child.isActive === false ? 'inactive' : ''}`}
                  onClick={() => {
                    console.log('🔥 Child card tapped:', child.fullName);
                    if (onOpenChild) onOpenChild(index);
                  }}
                >
                  <div className="child-avatar">
                    {child.photoURL ? (
                      <img src={child.photoURL} alt={child.fullName} />
                    ) : (
                      <span>👤</span>
                    )}
                    <span className={`status-dot ${child.isActive === false ? 'inactive' : 'active'}`}></span>
                  </div>
                  <div className="child-info">
                    <h4>{child.fullName}</h4>
                    <p>Student ID: {child.studentId}</p>
                    <p>Class: {child.className}</p>
                    <span className={`child-status ${child.isActive === false ? 'inactive' : 'active'}`}>
                      {child.isActive === false ? 'Inactive' : 'Active'}
                    </span>
                    <p className="view-detail-hint">Tap to view details →</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="events-section">
            <h3>Upcoming Events</h3>
            {upcomingEvents.length === 0 ? (
              <div className="empty-state">
                <p>No upcoming events</p>
              </div>
            ) : (
              <div className="events-list">
                {upcomingEvents.map((event, index) => (
                  <div key={index} className="event-card clickable" onClick={() => setSelectedEvent(event)}>
                    <div className="event-icon">
                      {getEventIcon(event.eventType)}
                    </div>
                    <div className="event-info">
                      <h4>{event.name}</h4>
                      <p className="event-type">{event.eventType}</p>
                      <p className="event-date">
                        {event.date?.toDate 
                          ? event.date.toDate().toLocaleDateString() 
                          : new Date(event.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="events-section">
            <h3>Past Events</h3>
            {pastEvents.length === 0 ? (
              <div className="empty-state compact">
                <p>No past events</p>
              </div>
            ) : (
              <div className="events-list">
                {pastEvents.map((event, index) => (
                  <div key={index} className="event-card past clickable" onClick={() => setSelectedEvent(event)}>
                    <div className="event-icon">
                      {getEventIcon(event.eventType)}
                    </div>
                    <div className="event-info">
                      <h4>{event.name}</h4>
                      <p className="event-type">{event.eventType}</p>
                      <p className="event-date">
                        {event.date?.toDate 
                          ? event.date.toDate().toLocaleDateString() 
                          : new Date(event.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <button className="quick-action-button" onClick={onOpenMessages}>
              <span className="quick-action-icon">💬</span>
              <div className="quick-action-info">
                <h4>Messages</h4>
                <p>Chat with teachers</p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function getEventIcon(eventType) {
  const icons = {
    'Marathon': '🏃',
    'Walkathon': '🚶',
    'Fun Run': '🏃',
    'Track and Field': '🏟️',
    'Field Trip': '🚌',
    'Assembly': '👥',
    'Sporting Event': '⚽',
    'Walk': '🚶',
    'Other': '📅'
  };
  return icons[eventType] || '📅';
}

function AttendanceTab({ children }) {
  return <ParentAttendanceView children={children} />;
}

function GradesTab({ children }) {
  return <ParentGradesView children={children} />;
}

function AssignmentsTab({ children }) {
  return <ParentAssignmentsView children={children} />;
}

function TimetableTab({ children }) {
  return <ParentTimetableView children={children} />;
}

function MessagesTab({ parentData }) {
  return <ParentMessagesView parentData={parentData} />;
}

function ProfileTab({ parentData, children }) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showEditProfile, setShowEditProfile] = useState(false);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError('');

    try {
      const user = auth.currentUser;
      if (!user) {
        setDeleteError('No authenticated user found. Please sign in again.');
        setIsDeleting(false);
        return;
      }

      const uid = user.uid;

      // 1. Delete the user's document from the `users` collection (if it exists)
      try {
        await deleteDoc(doc(db, 'users', uid));
        console.log('Deleted user document from users collection');
      } catch (err) {
        // It's okay if this document doesn't exist
        console.warn('Could not delete users document:', err.message);
      }

      // 2. Delete the user's document from the `parents` collection (if it exists)
      try {
        await deleteDoc(doc(db, 'parents', uid));
        console.log('Deleted user document from parents collection');
      } catch (err) {
        // It's okay if this document doesn't exist
        console.warn('Could not delete parents document:', err.message);
      }

      // 3. Delete the user's Firebase Auth account
      // Note: Firebase requires a recent sign-in for this operation.
      try {
        await deleteUser(user);
        console.log('Firebase Auth account deleted');
      } catch (err) {
        if (err.code === 'auth/requires-recent-login') {
          setDeleteError(
            'For security reasons, you must sign in again before deleting your account. Please log out, sign back in, and try again.'
          );
          setIsDeleting(false);
          return;
        }
        throw err;
      }

      // 4. Sign out the user (auth state change will redirect to login)
      try {
        await signOut(auth);
      } catch (err) {
        console.warn('Sign out after deletion failed:', err.message);
      }

      // The auth state change listener (in the parent component) will
      // handle redirecting back to the login screen automatically.
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError(
        error.message ||
          'An unexpected error occurred while deleting your account. Please try again.'
      );
      setIsDeleting(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="profile-header">
        <div className="profile-photo">
          {parentData?.photoURL ? (
            <img src={parentData.photoURL} alt="Profile" />
          ) : (
            <span className="placeholder-photo">👤</span>
          )}
          <span className="active-indicator"></span>
        </div>
        <div className="profile-name-section">
          <h2>{parentData?.fullName || 'Parent'}</h2>
          <p>{parentData?.email || 'N/A'}</p>
        </div>
        <button className="edit-profile-button" onClick={() => setShowEditProfile(true)}>
          <span>✏️</span>
          <span>Edit Profile</span>
        </button>
      </div>

      <div className="profile-section">
        <h3>Account Information</h3>
        <div className="profile-info">
          <div className="profile-field">
            <label>Full Name</label>
            <p>{parentData?.fullName || 'N/A'}</p>
          </div>
          <div className="profile-field">
            <label>Email</label>
            <p>{parentData?.email || 'N/A'}</p>
          </div>
          <div className="profile-field">
            <label>Phone Number</label>
            <p>{parentData?.phoneNumber || 'N/A'}</p>
          </div>
          <div className="profile-field">
            <label>Account Created</label>
            <p>{parentData?.createdAt ? new Date(parentData.createdAt).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>
      </div>

      <div className="profile-section">
        <h3>Linked Children ({children.length})</h3>
        {children.length === 0 ? (
          <div className="empty-state compact">
            <p>No children linked to your account</p>
          </div>
        ) : (
          <div className="linked-children-list">
            {children.map((child, index) => (
              <div key={index} className="linked-child-card">
                <div className="child-avatar">
                  {child.photoURL ? (
                    <img src={child.photoURL} alt={child.fullName} />
                  ) : (
                    <span>👤</span>
                  )}
                  <span className={`status-dot ${child.isActive === false ? 'inactive' : 'active'}`}></span>
                </div>
                <div className="child-info">
                  <h4>{child.fullName}</h4>
                  <p>Student ID: {child.studentId}</p>
                  <p>Class: {child.className || 'Not assigned'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="profile-section danger">
        <h3>Danger Zone</h3>
        <button className="delete-account-button" onClick={() => setShowDeleteConfirmation(true)}>
          Delete Account
        </button>
      </div>

      {showDeleteConfirmation && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirmation(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Account</h3>
            <p>This action cannot be undone. All your data will be permanently deleted.</p>
            {deleteError && (
              <p className="delete-error-message" style={{ color: '#d32f2f', marginTop: '8px' }}>
                {deleteError}
              </p>
            )}
            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setDeleteError('');
                  setShowDeleteConfirmation(false);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button className="delete-button" onClick={handleDeleteAccount} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditProfile && (
        <EditProfileModal
          parentData={parentData}
          onClose={() => setShowEditProfile(false)}
          onSaved={() => {
            setShowEditProfile(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

function EditProfileModal({ parentData, onClose, onSaved }) {
  const [firstName, setFirstName] = useState(parentData?.firstName || '');
  const [lastName, setLastName] = useState(parentData?.lastName || '');
  const [phoneNumber, setPhoneNumber] = useState(parentData?.phoneNumber || '');
  const [photoURL, setPhotoURL] = useState(parentData?.photoURL || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Photo must be less than 2MB');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoURL(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      let finalPhotoURL = parentData?.photoURL || null;

      // Upload new photo if selected
      if (photoFile) {
        const storageRef = ref(storage, `parentPhotos/${user.uid}/${Date.now()}_${photoFile.name}`);
        await uploadBytes(storageRef, photoFile);
        finalPhotoURL = await getDownloadURL(storageRef);
      }

      const fullName = `${firstName} ${lastName}`.trim();
      const updateData = {
        firstName,
        lastName,
        fullName,
        phoneNumber,
        photoURL: finalPhotoURL,
      };

      // Update parents collection
      await updateDoc(doc(db, 'parents', user.uid), updateData);

      // Update users collection if it exists
      try {
        await updateDoc(doc(db, 'users', user.uid), updateData);
      } catch (e) {
        // users doc may not exist for all parents
      }

      // Update Firebase Auth profile
      try {
        await updateProfile(user, { displayName: fullName, photoURL: finalPhotoURL });
      } catch (e) {
        // Non-critical
      }

      onSaved();
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <h3>Edit Profile</h3>
        {error && <p style={{ color: '#d32f2f', marginBottom: '12px' }}>{error}</p>}

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>Photo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="profile-photo" style={{ width: '64px', height: '64px' }}>
              {photoURL ? (
                <img src={photoURL} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <span className="placeholder-photo">👤</span>
              )}
            </div>
            <div>
              <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ fontSize: '13px' }} />
              {photoURL && (
                <button
                  onClick={() => { setPhotoURL(null); setPhotoFile(null); }}
                  style={{ display: 'block', marginTop: '4px', background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '13px' }}
                >
                  Remove Photo
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>First Name</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Phone Number</label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', color: '#888' }}>Email (read-only)</label>
          <input
            type="email"
            value={parentData?.email || ''}
            disabled
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px', background: '#f5f5f5', color: '#888' }}
          />
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="delete-button" onClick={handleSave} disabled={saving} style={{ background: '#14532d' }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChildTab({ children, parentData, selectedChildIndex = 0, onSelectChild }) {
  const [selectedSubTab, setSelectedSubTab] = useState(0);
  const [showLinking, setShowLinking] = useState(false);

  const subTabs = ['Overview', 'Grades', 'Attendance', 'Assignments', 'Timetable', 'Online', 'Behavior'];

  if (children.length === 0) {
    return (
      <div className="mobile-child-empty">
        <div className="child-empty-icon">👨‍👩‍👧‍👦</div>
        <h3>No Children Linked</h3>
        <p>Add your child using their Student ID to view their progress</p>
        <button className="primary-button" onClick={() => setShowLinking(true)}>
          Link a Child
        </button>
        {showLinking && (
          <ChildLinking
            parentData={parentData}
            onChildLinked={() => setShowLinking(false)}
          />
        )}
      </div>
    );
  }

  const selectedChild = children[Math.min(selectedChildIndex, children.length - 1)];

  return (
    <div className="mobile-child-tab">
      {children.length > 1 && (
        <div className="child-picker-scroll">
          {children.map((child, index) => (
            <button
              key={index}
              className={`child-picker-btn ${selectedChildIndex === index ? 'active' : ''}`}
              onClick={() => { if (onSelectChild) onSelectChild(index); setSelectedSubTab(0); }}
            >
              <div className="child-picker-avatar">
                {child.photoURL ? (
                  <img src={child.photoURL} alt={child.fullName} />
                ) : (
                  <span>👤</span>
                )}
                <span className={`picker-dot ${child.isActive === false ? 'inactive' : 'active'}`}></span>
              </div>
              <span className="child-picker-name">
                {child.firstName || (child.fullName || '').split(' ')[0] || 'Child'}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="child-detail-header">
        <div className="child-detail-avatar">
          {selectedChild.photoURL ? (
            <img src={selectedChild.photoURL} alt={selectedChild.fullName} />
          ) : (
            <span>👤</span>
          )}
          <span className={`status-dot ${selectedChild.isActive === false ? 'inactive' : 'active'}`}></span>
        </div>
        <div>
          <h3 className="child-detail-name">{selectedChild.fullName}</h3>
          <p className="child-detail-id">ID: {selectedChild.studentId}</p>
          <span className={`child-status ${selectedChild.isActive === false ? 'inactive' : 'active'}`}>
            {selectedChild.isActive === false ? 'Inactive' : 'Active'}
          </span>
        </div>
      </div>

      <div className="child-segment-bar">
        <div className="child-segment-track">
          {subTabs.map((tab, index) => (
            <button
              key={index}
              className={`child-segment-btn ${selectedSubTab === index ? 'active' : ''}`}
              onClick={() => setSelectedSubTab(index)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="child-subtab-content">
        {selectedSubTab === 0 && <ChildOverviewSubTab child={selectedChild} />}
        {selectedSubTab === 1 && <GradesTab children={[selectedChild]} />}
        {selectedSubTab === 2 && <AttendanceTab children={[selectedChild]} />}
        {selectedSubTab === 3 && <AssignmentsTab children={[selectedChild]} />}
        {selectedSubTab === 4 && <TimetableTab children={[selectedChild]} />}
        {selectedSubTab === 5 && <ParentOnlineClassesView key={selectedChild.studentId} children={[selectedChild]} />}
        {selectedSubTab === 6 && <StudentBehaviorView key={selectedChild.studentId} mode="parent" studentIds={[selectedChild.studentId]} />}
      </div>
    </div>
  );
}

function ChildOverviewSubTab({ child }) {
  const [recentGrades, setRecentGrades] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState({ present: 0, absent: 0, late: 0, excused: 0, rate: 0 });
  const [upcomingAssignments, setUpcomingAssignments] = useState([]);
  const [todayTimetable, setTodayTimetable] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOverviewData = useCallback(async (c) => {
    setLoading(true);
    try {
      const now = new Date();
      const todayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][now.getDay()];

      // Load assignments + grades
      const assignmentsSub = collection(db, 'classes', c.classId, 'assignments');
      const assignmentsSnap = await getDocs(assignmentsSub);
      const grades = [];
      const upcoming = [];

      for (const aDoc of assignmentsSnap.docs) {
        const aData = aDoc.data();
        if (aData.isInClassOnly === true) continue;

        // Check grades
        try {
          const gradesSub = collection(db, 'classes', c.classId, 'assignments', aDoc.id, 'grades');
          const gradesSnap = await getDocs(gradesSub);
          gradesSnap.forEach(gDoc => {
            const gData = gDoc.data();
            if (gData.studentId === c.id || gData.studentId === c.studentId) {
              grades.push({ assignmentId: aDoc.id, assignmentTitle: aData.title, ...gData });
            }
          });
        } catch (e) { /* skip */ }

        // Check upcoming
        const dueDate = aData.dueDate?.toDate ? new Date(aData.dueDate.toDate()) : (aData.dueDate ? new Date(aData.dueDate) : null);
        if (dueDate && dueDate > now) {
          upcoming.push({ id: aDoc.id, title: aData.title, dueDate, assignmentType: aData.assignmentType });
        }
      }

      // Sort and take recent grades (last 3)
      grades.sort((a, b) => {
        const dateA = a.gradedAt?.toDate ? a.gradedAt.toDate() : new Date(a.gradedAt || 0);
        const dateB = b.gradedAt?.toDate ? b.gradedAt.toDate() : new Date(b.gradedAt || 0);
        return dateB - dateA;
      });
      setRecentGrades(grades.slice(0, 3));

      // Sort and take upcoming assignments (next 3)
      upcoming.sort((a, b) => a.dueDate - b.dueDate);
      setUpcomingAssignments(upcoming.slice(0, 3));

      // Load attendance summary
      try {
        const attendanceSub = collection(db, 'classes', c.classId, 'attendanceRecords');
        const attendanceSnap = await getDocs(attendanceSub);
        let present = 0, absent = 0, late = 0, excused = 0;
        attendanceSnap.forEach(doc => {
          const data = doc.data();
          if (data.studentId === c.id || data.studentId === c.studentId) {
            const status = (data.status || '').toLowerCase();
            if (status === 'present') present++;
            else if (status === 'absent') absent++;
            else if (status === 'late') late++;
            else if (status === 'excused') excused++;
          }
        });
        const total = present + absent + late + excused;
        const rate = total > 0 ? Math.round(((present + excused) / total) * 100) : 0;
        setAttendanceSummary({ present, absent, late, excused, rate });
      } catch (e) { /* skip */ }

      // Load today's timetable
      try {
        const timetableDoc = await getDoc(doc(db, 'timetables', c.classId));
        if (timetableDoc.exists()) {
          const tData = timetableDoc.data();
          const allEntries = tData.entries || [];
          const todayEntries = allEntries
            .filter(e => e.dayOfWeek === todayName)
            .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
            .slice(0, 3);
          setTodayTimetable(todayEntries);
        }
      } catch (e) { /* skip */ }
    } catch (error) {
      console.error('Error loading overview data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (child?.classId) {
      loadOverviewData(child);
    } else {
      setLoading(false);
    }
  }, [child, loadOverviewData]);

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return '#10b981';
    if (percentage >= 80) return '#22c55e';
    if (percentage >= 70) return '#f59e0b';
    if (percentage >= 60) return '#f97316';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div className="child-overview-subtab">
        <div className="loading-container" style={{ padding: '24px' }}>
          <div className="spinner"></div>
          <p>Loading overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="child-overview-subtab">
      <div className="overview-info-card">
        <div className="overview-row">
          <span className="overview-label">Full Name</span>
          <span className="overview-value">{child.fullName || 'N/A'}</span>
        </div>
        <div className="overview-row">
          <span className="overview-label">Student ID</span>
          <span className="overview-value">{child.studentId || 'N/A'}</span>
        </div>
        <div className="overview-row">
          <span className="overview-label">Class</span>
          <span className="overview-value">{child.className || 'Not Assigned'}</span>
        </div>
        <div className="overview-row">
          <span className="overview-label">Homeroom Teacher</span>
          <span className="overview-value">{child.teacherName || 'N/A'}</span>
        </div>
        {child.subjectTeachers && child.subjectTeachers.length > 0 && (
          <div className="overview-row">
            <span className="overview-label">Subject Teachers</span>
            <div className="overview-value subject-teachers-list">
              {child.subjectTeachers.map((st, i) => (
                <span key={i} className="subject-teacher-badge">
                  <strong>{st.subject}</strong>: {st.teacherName}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="overview-row">
          <span className="overview-label">Status</span>
          <span className={`overview-status ${child.isActive === false ? 'inactive' : 'active'}`}>
            {child.isActive === false ? 'Inactive' : 'Active'}
          </span>
        </div>
      </div>

      {/* Recent Grades Card */}
      <div className="overview-summary-card">
        <h4>📊 Recent Grades</h4>
        {recentGrades.length === 0 ? (
          <p className="overview-empty">No grades yet</p>
        ) : (
          <div className="overview-grades-list">
            {recentGrades.map((grade, i) => {
              const pct = grade.maxScore > 0 ? Math.round((grade.score / grade.maxScore) * 100) : 0;
              return (
                <div key={i} className="overview-grade-row">
                  <span className="overview-grade-title">{grade.assignmentTitle}</span>
                  <span className="overview-grade-score" style={{ color: getGradeColor(pct) }}>
                    {grade.score}/{grade.maxScore} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Attendance Summary Card */}
      <div className="overview-summary-card">
        <h4>📅 Attendance</h4>
        <div className="overview-attendance-grid">
          <div className="overview-attendance-stat">
            <span className="stat-value present">{attendanceSummary.present}</span>
            <span className="stat-label">Present</span>
          </div>
          <div className="overview-attendance-stat">
            <span className="stat-value absent">{attendanceSummary.absent}</span>
            <span className="stat-label">Absent</span>
          </div>
          <div className="overview-attendance-stat">
            <span className="stat-value late">{attendanceSummary.late}</span>
            <span className="stat-label">Late</span>
          </div>
          <div className="overview-attendance-stat">
            <span className="stat-value rate" style={{ color: getGradeColor(attendanceSummary.rate) }}>
              {attendanceSummary.rate}%
            </span>
            <span className="stat-label">Rate</span>
          </div>
        </div>
      </div>

      {/* Upcoming Assignments Card */}
      <div className="overview-summary-card">
        <h4>📝 Upcoming Assignments</h4>
        {upcomingAssignments.length === 0 ? (
          <p className="overview-empty">No upcoming assignments</p>
        ) : (
          <div className="overview-assignments-list">
            {upcomingAssignments.map((a, i) => {
              const daysLeft = Math.ceil((a.dueDate - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div key={i} className="overview-assignment-row">
                  <span className="overview-assignment-title">{a.title}</span>
                  <span className="overview-assignment-due" style={{ color: daysLeft <= 1 ? '#ef4444' : '#6b7280' }}>
                    {daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft} days`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Today's Timetable Card */}
      <div className="overview-summary-card">
        <h4>🗓️ Today's Classes</h4>
        {todayTimetable.length === 0 ? (
          <p className="overview-empty">No classes today</p>
        ) : (
          <div className="overview-timetable-list">
            {todayTimetable.map((entry, i) => (
              <div key={i} className="overview-timetable-row">
                <span className="overview-timetable-subject">
                  {entry.entryType === 'lunch' ? '🍽️ Lunch' :
                   entry.entryType === 'break' ? '☕ Break' :
                   entry.subjectName || 'Free'}
                </span>
                <span className="overview-timetable-time">{entry.startTime} - {entry.endTime}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="overview-nav-cards">
        <div className="overview-nav-card">
          <span className="overview-nav-icon">📊</span>
          <span>Grades</span>
          <span className="overview-nav-arrow">›</span>
        </div>
        <div className="overview-nav-card">
          <span className="overview-nav-icon">📅</span>
          <span>Attendance</span>
          <span className="overview-nav-arrow">›</span>
        </div>
        <div className="overview-nav-card">
          <span className="overview-nav-icon">📝</span>
          <span>Assignments</span>
          <span className="overview-nav-arrow">›</span>
        </div>
      </div>
    </div>
  );
}

export default ParentDashboard;
